import { createSlice } from "@reduxjs/toolkit";
import { api } from "service/api";
import { UserMgmtApi } from "service/apiVariables";
import { Toast } from "service/toast";
import { addQuery } from "utils";
import { setLoader } from "./loader";

const handleApiError = (error) => {
  Toast({
    type: "error",
    message: error?.message || "Something went wrong",
  });

  throw error;
};

export const userMgmtSlice = createSlice({
  name: "userMgmt",
  initialState: {
    employees: null,
    isLoading: true,
    userData: {},
    tradeData: "",
    generatedPwd: null,
    projects: [],
    profilePicDetails: "",
    activeRole: null,
    inactiveRole: null,
    activeProject: null,
    inactiveProject: null,
  },
  reducers: {
    saveTradeData: (state, action) => {
      state.tradeData = action.payload;
    },

    saveEmployeesData: (state, action) => {
      state.employees = action.payload;
      state.isLoading = false;
    },

    saveUserData: (state, action) => {
      state.userData = action.payload;
      state.isLoading = false;
    },

    saveGeneratedPwd: (state, action) => {
      state.generatedPwd = action.payload;
      state.isLoading = false;
    },

    saveProjects: (state, action) => {
      state.projects = action.payload;
      state.isLoading = false;
    },

    saveProfilePicData: (state, action) => {
      state.profilePicDetails = action.payload;
    },

    setSelectedActiveRole: (state, action) => {
      state.activeRole = action.payload;
    },

    setSelectedInactiveRole: (state, action) => {
      state.inactiveRole = action.payload;
    },

    setSelectedActiveProject: (state, action) => {
      state.activeProject = action.payload;
    },

    setSelectedInactiveProject: (state, action) => {
      state.inactiveProject = action.payload;
    },
  },
});

// get all employees
export const getAllEmployeesList = (query) => async (dispatch) => {
  try {
    addQuery(query, UserMgmtApi.getAllEmployeeApi);

    const { data } = await api({
      ...UserMgmtApi.getAllEmployeeApi,
    });

    dispatch(saveEmployeesData(data));
    dispatch(setLoader(false));

    return data;
  } catch (error) {
    dispatch(setLoader(false));

    if (error?.status === 400) {
      dispatch(saveEmployeesData(error?.data));
      return error?.data;
    }

    handleApiError(error);
  }
};

// get individual employee detail
export const getIndividualEmpDetails = (id) => async (dispatch) => {
  try {
    UserMgmtApi.getEmployeeApi.url_id = id;

    const response = await api({
      ...UserMgmtApi.getEmployeeApi,
    });

    const userData = response?.data;

    dispatch(saveUserData(userData));

    return userData;
  } catch (error) {
    handleApiError(error);
  }
};

// generate password
export const generatePassword = (id) => async (dispatch) => {
  try {
    UserMgmtApi.generatePasswordApi.url_id = id;

    const data = await api({
      ...UserMgmtApi.generatePasswordApi,
    });

    Toast({
      type: "success",
      message: data?.message,
    });

    dispatch(saveGeneratedPwd(data));

    return data;
  } catch (error) {
    handleApiError(error);
  }
};

// update user status
export const updateUserStatus = (id, type) => async () => {
  try {
    UserMgmtApi.updateUserStatusApi.type = type;
    UserMgmtApi.updateUserStatusApi.url_id = id;

    const data = await api({
      ...UserMgmtApi.updateUserStatusApi,
    });

    return data;
  } catch (error) {
    handleApiError(error);
  }
};

// delete employee
export const deleteEmployee = (id) => async () => {
  try {
    UserMgmtApi.deleteUserApi.url_id = id;

    const data = await api({
      ...UserMgmtApi.deleteUserApi,
    });

    Toast({
      type: "success",
      message: data?.message,
    });

    return data;
  } catch (error) {
    handleApiError(error);
  }
};

// get trade dropdown
export const getTradeDropDownAct = () => async (dispatch) => {
  try {
    const data = await api({
      ...UserMgmtApi.getTradeDropDown,
    });

    dispatch(saveTradeData(data));

    return data;
  } catch (error) {
    handleApiError(error);
  }
};

// employee file upload
export const employeeFileUpload = (body) => async () => {
  try {
    const response = await api({
      ...UserMgmtApi.employeeUploadFileApi,
      body,
    });

    Toast({
      type: "success",
      message: response?.message,
    });

    return response?.data;
  } catch (error) {
    handleApiError(error);
  }
};

// profile pic upload
export const employeeProfilePicUpload = (body) => async (dispatch) => {
  try {
    const response = await api({
      ...UserMgmtApi.imageUploadApi,
      body,
    });

    dispatch(saveProfilePicData(response?.data));

    return response?.data;
  } catch (error) {
    handleApiError(error);
  }
};

// add employee
export const addEmployeeAct = (body) => async () => {
  const data = await api({
    ...UserMgmtApi.addEmployeeApi,
    body,
  });

  return data;
};

// update employee
export const updateEmployeeAct = (body) => async () => {
  const response = await api({
    ...UserMgmtApi.updateEmployeeApi,
    body,
  });

  return response?.data;
};

// list projects
export const getProjectsListAct = () => async () => {
  try {
    const data = await api({
      ...UserMgmtApi.getProjectsListApi,
    });

    return data;
  } catch (error) {
    handleApiError(error);
  }
};

// list assigned projects
export const getAssignedProjectsList = () => async () => {
  try {
    const data = await api({
      ...UserMgmtApi.getAssignedProjectsApi,
    });

    return data;
  } catch (error) {
    handleApiError(error);
  }
};

// reports assigned projects
export const getReportsAssignedProjectsList = (query) => async () => {
  try {
    addQuery(query, UserMgmtApi.getReportsAssignedProjectsApi);

    const data = await api({
      ...UserMgmtApi.getReportsAssignedProjectsApi,
    });

    return data;
  } catch (error) {
    handleApiError(error);
  }
};

// multiple status update
export const multipleStatusUpdateAct = (body) => async () => {
  try {
    const response = await api({
      ...UserMgmtApi.multipleStatusUpdate,
      body,
    });

    Toast({
      type: "success",
      message: response?.message,
    });

    return response?.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const sendEmailVerificationCodeAct = (body) => async () => {
  try {
    const response = await api({
      ...UserMgmtApi.sendEmailOTPNewUser,
      body,
    });

    Toast({
      type: "success",
      message: response?.message || "Verification code sent successfully",
    });

    return response?.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const verifyEmailVerificationCodeAct = (body) => async () => {
  try {
    const response = await api({
      ...UserMgmtApi.verifyEmailOTP,
      body,
    });

    Toast({
      type: "success",
      message: response?.message || "Email verified successfully",
    });

    return response?.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// existing employee
export const getExistingEmployee = (query) => async () => {
  try {
    addQuery(query, UserMgmtApi.getExistingEmpApi);

    const { data, message } = await api({
      ...UserMgmtApi.getExistingEmpApi,
    });

    return { data: data, mess: message };
  } catch (error) {
    handleApiError(error);
  }
};

// download users
export const downloadUsers = (query) => async () => {
  try {
    addQuery(query, UserMgmtApi.getdownloadUserApi);

    const data = await api({
      ...UserMgmtApi.getdownloadUserApi,
      method: "get",
    });

    return data;
  } catch (error) {
    console.error("Failed to download users:", error);
    return null;
  }
};

export const {
  saveTradeData,
  saveEmployeesData,
  saveUserData,
  saveGeneratedPwd,
  saveProjects,
  saveProfilePicData,
  setSelectedActiveRole,
  setSelectedInactiveRole,
  setSelectedActiveProject,
  setSelectedInactiveProject,
} = userMgmtSlice.actions;

export default userMgmtSlice.reducer;
